import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AiAssistantChat } from "@/components/AiAssistantChat";
import { AuthProvider } from "./contexts/AuthContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { NativelyInit } from "@/components/NativelyInit";
import { useAnalytics } from "@/hooks/useAnalytics";
import Index from "./pages/Index";
import FieldGuide from "./pages/FieldGuide";
import ProducerGuide from "./pages/fieldguide/ProducerGuide";
import CrewGuide from "./pages/fieldguide/CrewGuide";
import QuickReference from "./pages/fieldguide/QuickReference";
import Troubleshooting from "./pages/fieldguide/Troubleshooting";
import Contact from "./pages/Contact";
import UserCases from "./pages/UserCases";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import Dashboard from "./pages/Dashboard";
import ProducerLogin from "./pages/ProducerLogin";
import GetCrewApp from "./pages/GetCrewApp";
import VerifyEmail from "./pages/VerifyEmail";
import CreateEditBriefing from "./pages/CreateEditBriefing";
import ReviewPublishBriefing from "./pages/ReviewPublishBriefing";
import BriefingArchive from "./pages/BriefingArchive";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import ManageCrew from "./pages/ManageCrew";
import LiveTracking from "./pages/LiveTracking";
import Analytics from "./pages/Analytics";
import UpgradePage from "./pages/UpgradePage";
import ChooseTrial from "./pages/ChooseTrial";
import RequestPasswordReset from "./pages/auth/RequestPasswordReset";
import ResetPassword from "./pages/auth/ResetPassword";
import PasswordResetSuccess from "./pages/auth/PasswordResetSuccess";
import AuthConfirm from "./pages/auth/AuthConfirm";
import ConfirmEmailChange from "./pages/ConfirmEmailChange";
import UserManagement from "./pages/admin/UserManagement";
import UserDetail from "./pages/admin/UserDetail";
import DiscountCodes from "./pages/admin/DiscountCodes";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ProductionDashboard from "./pages/ProductionDashboard";
import BriefingView from "./pages/BriefingView";
import Schedule from "./pages/Schedule";
import MyTeam from "./pages/MyTeam";
import EarlyAccess from "./pages/EarlyAccess";
import { ProducerRoute } from "./components/auth/ProducerRoute";
import { ProductionRoute } from "./components/auth/ProductionRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import CoProducerAccess from "./pages/CoProducerAccess";
import ScheduleReview from "./pages/ScheduleReview";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: false, // Prevent automatic retries to avoid error spam
      refetchOnWindowFocus: false, // Prevent auto-refetch on tab focus
      refetchOnReconnect: false, // Prevent auto-refetch on network reconnect
      networkMode: "online", // Only fetch when online - fail fast
    },
  },
});

const AnimatedRoutes = () => {
  const location = useLocation();
  useAnalytics();

  // The producer AI assistant stays off the one crew-facing surface left:
  // /crew-login, which hands crew the native app.
  const isCrewRoute = location.pathname.startsWith("/crew");
  // The public client-review page must not show the producer AI assistant.
  const isClientReview = location.pathname === "/schedule-review";
  const showAssistant = !isCrewRoute && !isClientReview;

  return (
    <>
      {/* Snap navigation — no AnimatePresence + mode="wait", no
          key={location.pathname}. The previous setup forced the
          outgoing page to fully exit before the incoming one mounted,
          which on Safari produced a visible ~400ms blank window on
          every link click (the "flash/reload" symptom). Modern
          productivity tools (Linear, Notion, Figma, Gmail) all use
          instant navigation for this same reason. PageTransition is
          also neutralised below so the per-page fade-in doesn't
          reintroduce a subtler version of the flash. */}
      <Routes location={location}>
        <Route path="/" element={<Index />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/user-cases" element={<UserCases />} />
        <Route path="/field-guide" element={<FieldGuide />} />
        <Route path="/field-guide/producer" element={<ProducerGuide />} />
        <Route path="/field-guide/crew" element={<CrewGuide />} />
        <Route path="/field-guide/quick-reference" element={<QuickReference />} />
        <Route path="/field-guide/troubleshooting" element={<Troubleshooting />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/producer-login" element={<ProducerLogin />} />
        {/* Crew work in the native app; the browser only points them at it.
            Still /crew-login because that path is a registered universal
            link (AASA + app.config.ts) and the target of every crew invite
            and verification email — on a phone with the app installed, iOS
            and Android hand the URL to the app and this page never renders. */}
        <Route path="/crew-login" element={<GetCrewApp />} />
        <Route path="/early-access" element={<EarlyAccess />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* Fallback for crew users whose verify link landed in the
              browser (app not installed, AASA cache miss). Same
              component — token is carried in the query string. */}
        <Route path="/crew-verify-email" element={<VerifyEmail />} />
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/choose-trial" element={<ChooseTrial />} />
        <Route path="/auth/confirm" element={<AuthConfirm />} />
        <Route path="/auth/request-password-reset" element={<RequestPasswordReset />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/co-producer/access" element={<CoProducerAccess />} />
        {/* Public, unauthenticated client schedule review (secret token link). */}
        <Route path="/schedule-review" element={<ScheduleReview />} />
        <Route path="/auth/password-reset-success" element={<PasswordResetSuccess />} />
        <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route element={<AdminRoute />}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/users/:userId" element={<UserDetail />} />
          <Route path="/admin/discount-codes" element={<DiscountCodes />} />
        </Route>

        {/* Producer Routes - Protected (the "layer above" a single production:
            overview, settings, my-team — co-producers are bounced away from these) */}
        <Route element={<ProducerRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-team" element={<MyTeam />} />
          <Route path="/settings" element={<Settings />} />

          {/* Owner-only: creating a new production / the all-productions
              archive are above the single-production layer. */}
          <Route path="/briefings/create" element={<CreateEditBriefing />} />
          <Route path="/briefings/archive" element={<BriefingArchive />} />

          {/* Param-less legacy routes (rely on a globally-selected production) */}
          <Route path="/manage-crew" element={<ManageCrew />} />
          <Route path="/live-tracking" element={<LiveTracking />} />
          <Route path="/analytics" element={<Analytics />} />
        </Route>

        {/* Single-production routes — owners, admins, and the production's
            co-producers. Co-producers are confined here by ProductionRoute. */}
        <Route element={<ProductionRoute />}>
          <Route path="/productions/:productionId" element={<ProductionDashboard />} />
          <Route path="/productions/:productionId/live-tracking" element={<LiveTracking />} />
          <Route path="/productions/:productionId/crew" element={<ManageCrew />} />
          <Route path="/productions/:productionId/analytics" element={<Analytics />} />
          <Route path="/productions/:productionId/briefing" element={<BriefingView />} />
          <Route path="/productions/:productionId/schedule" element={<Schedule />} />

          {/* The full briefing editor / review flow for one production. */}
          <Route path="/briefings/:id" element={<ReviewPublishBriefing />} />
          <Route path="/briefings/:id/edit" element={<CreateEditBriefing />} />
          <Route path="/briefings/:id/review" element={<ReviewPublishBriefing />} />
        </Route>

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showAssistant && <AiAssistantChat />}
    </>
  );
};

const App = () => (
  <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SidebarProvider>
          <TooltipProvider>
            <NativelyInit />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AnimatedRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </SidebarProvider>
      </AuthProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
